import type { Locale } from '@seek/melways-sites';
import {
  Box,
  Stack,
  TabPanel,
  TabPanels,
  TabsProvider,
  useResponsiveValue,
} from 'braid-design-system';
import { observer } from 'mobx-react';
import type { FC, PropsWithChildren } from 'react';
import { StringParam, useQueryParam } from 'use-query-params';

import PreviousApplications from 'src/App/AppPageBlock/ProfileDrawer/PreviousApplications/PreviousApplications';
import { ProfileBody } from 'src/App/AppPageBlock/ProfileDrawer/Profile/ProfileBody';
import type { ApplicationNoteV2 } from 'src/App/AppPageBlock/graphql/hirer-types.generated';
import { useUserActionStatus } from 'src/components/common/UserActionStatusContext';
import type { mapToSimplifiedAnswers } from 'src/components/list/JobApplicationCard/RoleRequirementsView/RoleRequirement/mapToSimplifiedAnswers';
import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';
import { TRACKING_CANDIDATE_DETAILS } from 'src/constants/pageTrackingData';
import type { PageConfig } from 'src/libs/PageConfig';
import type { IAttachment } from 'src/stores/JobApplicationStore/Attachment';
import {
  attachmentUrlV2,
  mapStoreAttachmentTypeToRequestInput,
} from 'src/utils/attachmentUrls';
import {
  convertToIsVerifiedId,
  convertToReferencesReceived,
} from 'src/utils/eventMappers';
import {
  trackClickedNotesTab,
  trackClickedProfileTab,
  trackClickedEmailsTab,
  trackClickedResumeTab,
  trackClickedCoverLetterTab,
  trackClickedSelectionCriteriaTab,
  trackClickedPreviousApplicationsTab,
} from 'src/utils/events';
import { sendHotjarTag } from 'src/utils/hotjar';
import { sortStoreAttachments } from 'src/utils/sortStoreAttachments';

import PdfViewer from '../../../../../components/details/PdfViewer';
import ProspectEmail from '../../../../../components/details/ProspectEmail';
import { HOTJAR_TAG_PROFILE } from '../../../../../constants/hotjar';
import { JOB_APPLICATION_TAB } from '../../../../../constants/tabs';
import { CANDIDATE_PROFILE_DRAWER_TAB_PARAM_KEY } from '../../../../../pages/CandidateManagementPage/queryParams';
import type { IJobApplication } from '../../../../../stores/JobApplicationStore/JobApplication';
import { getReferenceCheckCounts } from '../../JobApplicationDetails/getReferenceCheckCounts';
import type { Attachment } from '../../NotesAttachments/NotesAttachmentList/NotesAttachmentList';
import NotesAttachments from '../../NotesAttachments/NotesAttachments';
import {
  UnlockReferenceCheckAlert,
  ReferencesEmptyState,
  PurchaseReferenceCheckAddonAlert,
} from '../../References';
import { Verifications } from '../../Verifications';
import type { CheckReferencesActionOriginsProfileDrawer } from '../../createPageAnalytics';
import type transformProfileToComponentProps from '../Profile/service/transformProfile';
import TabSelect, { type TabSelectOption } from '../TabSelect';
import { getApplicationSeekReferenceStatus } from '../getApplicationSeekReferenceStatus';

import { useApplicationStatsQuery } from './DetailsTabView.hirer.generated';
import { useGetDetailsTabs } from './hooks/useGetDetailsTabs';

interface Props {
  jobApplication: IJobApplication;
  locale: Locale;
  shouldShowVerifiedCredentials?: boolean;
  jobId: string;
  addCustomError: PageConfig['dataDogRum']['addCustomError'];
  transformedProfile: ReturnType<typeof transformProfileToComponentProps>;
  applicationQuestions: ReturnType<typeof mapToSimplifiedAnswers>;
  userId: string;
  onClickReferencesTab: () => void;
  onClickVerificationsTab: () => void;
  onClickVerificationsAlertLearnMore: () => void;
  onCloseVerificationsAlert: () => void;
  onClickVerificationsTabFooterLearnMore: () => void;
  onProfileBodyVerificationsAlertMount: () => void;
  onClickProfileBodyVerificationsTab: () => void;
  onClickProfileTabVerificationsLearnMore: () => void;
  onClickCareerHistoryWhyThisCouldHappen: () => void;
  referenceChecks:
    | {
        case: 'REQUESTED';
      }
    | {
        case: 'NOT_REQUESTED';
      }
    | {
        case: 'LIMITED_AVAILABLE';
        canUnlockStandardReferenceCheck: boolean;
        onClick: (props: {
          actionOrigin: CheckReferencesActionOriginsProfileDrawer;
        }) => void;
        counts: {
          finishedCount: number;
          totalCount: number;
        };
      }
    | {
        case: 'STANDARD_AVAILABLE';
        counts: {
          finishedCount: number;
          totalCount: number;
        };
      };
}

export interface DetailTabSelectOption extends TabSelectOption {
  attachment?: IAttachment;
  counts?: number;
  value: JOB_APPLICATION_TAB;
}

const DetailsTabView = ({
  jobApplication,
  locale,
  shouldShowVerifiedCredentials,
  jobId,
  addCustomError,
  transformedProfile,
  applicationQuestions,
  userId,
  onClickReferencesTab,
  onClickVerificationsTab,
  onClickVerificationsAlertLearnMore,
  onCloseVerificationsAlert,
  onClickVerificationsTabFooterLearnMore,
  onProfileBodyVerificationsAlertMount,
  onClickProfileBodyVerificationsTab,
  onClickProfileTabVerificationsLearnMore,
  onClickCareerHistoryWhyThisCouldHappen,
  referenceChecks,
}: Props) => {
  const {
    notes,
    attachmentsStore,
    prospectEmailStore,
    candidateStore,
    profile,
    verifications,
  } = jobApplication;

  const { language } = useAppConfig();

  const isSeekReferenceApplication = referenceChecks.case !== 'NOT_REQUESTED';

  const isVerifiedId = convertToIsVerifiedId(jobApplication.profile);

  const referenceCheckCounts = getReferenceCheckCounts(referenceChecks);
  const referencesReceived = convertToReferencesReceived(referenceCheckCounts);
  const isMobile =
    useResponsiveValue()({ mobile: true, tablet: false }) ?? false;

  const updateStoreWithNotesAttachmentData = (
    attachmentData: Attachment[],
    notesData: ApplicationNoteV2[],
  ) => {
    attachmentsStore.setAttachmentList({ data: attachmentData });
    notes.setNoteList({ data: notesData });
  };

  const [, setTabQueryParam] = useQueryParam(
    CANDIDATE_PROFILE_DRAWER_TAB_PARAM_KEY,
    StringParam,
  );

  const { getVerificationsTabNewBadgeStatus } = useUserActionStatus(
    'verifications-tab-new-badge',
  );

  const { data } = useApplicationStatsQuery({
    variables: { input: { jobId, adcentreProspectId: jobApplication.id } },
  });

  const applicationResponse =
    data?.applicationByAdcentreProspectId.__typename ===
    'ApplicationResponseSuccess'
      ? data.applicationByAdcentreProspectId.result
      : null;

  const applicationProfile =
    applicationResponse?.profile.__typename ===
    'ApplicationProfileResponseSuccess'
      ? applicationResponse.profile.result
      : null;

  const verificationStats =
    applicationProfile?.verificationsStats.__typename ===
    'ApplicationProfileVerificationsStatsSuccess'
      ? applicationProfile.verificationsStats
      : null;

  const tabs = useGetDetailsTabs({
    jobApplication,
    isMobile,
    isSeekReferenceApplication,
    finishedReferencesCount: referenceCheckCounts?.finishedCount ?? 0,
    totalVerificationsCount: verificationStats?.totalCount ?? 0,
    showVerificationsTabNewBadge:
      getVerificationsTabNewBadgeStatus() === 'visible',
  });

  const getDocumentTrackingAttributes = (tab: JOB_APPLICATION_TAB) => {
    const doc = tabs.find((x) => x.value === tab)?.attachment;
    return {
      documentType: doc?.typeName,
      attachmentId: doc?.attachmentId,
      ...jobApplication.baseTrackingAttributes,
    };
  };

  const onTabChange = (_selectedIndex: number, tab?: string) => {
    jobApplication.selectTabInDetailView(tab as JOB_APPLICATION_TAB);
    switch (tab) {
      case JOB_APPLICATION_TAB.NOTES_ATTACHMENTS:
        trackClickedNotesTab(notes.trackingAttributes, {
          currentPage: TRACKING_CANDIDATE_DETAILS,
        });
        setTabQueryParam(JOB_APPLICATION_TAB.NOTES_ATTACHMENTS);
        return;
      case JOB_APPLICATION_TAB.PROFILE:
        trackClickedProfileTab(jobApplication.baseTrackingAttributes);
        sendHotjarTag(HOTJAR_TAG_PROFILE);
        setTabQueryParam(JOB_APPLICATION_TAB.PROFILE);
        return;
      case JOB_APPLICATION_TAB.PREVIOUS_APPLICATIONS:
        trackClickedPreviousApplicationsTab(
          jobApplication.baseTrackingAttributes,
        );
        setTabQueryParam(JOB_APPLICATION_TAB.PREVIOUS_APPLICATIONS);
        return;
      case JOB_APPLICATION_TAB.EMAILS:
        trackClickedEmailsTab(prospectEmailStore.trackingAttributes);
        setTabQueryParam(JOB_APPLICATION_TAB.EMAILS);
        return;
      case JOB_APPLICATION_TAB.RESUME:
        trackClickedResumeTab(getDocumentTrackingAttributes(tab));
        setTabQueryParam(JOB_APPLICATION_TAB.RESUME);
        return;
      case JOB_APPLICATION_TAB.COVER_LETTER:
        trackClickedCoverLetterTab(getDocumentTrackingAttributes(tab));
        setTabQueryParam(JOB_APPLICATION_TAB.COVER_LETTER);
        return;
      case JOB_APPLICATION_TAB.SELECTION_CRITERIA:
        trackClickedSelectionCriteriaTab(getDocumentTrackingAttributes(tab));
        setTabQueryParam(JOB_APPLICATION_TAB.SELECTION_CRITERIA);
        return;
      case JOB_APPLICATION_TAB.REFERENCES:
        onClickReferencesTab();
        setTabQueryParam(JOB_APPLICATION_TAB.REFERENCES);
        return;
      case JOB_APPLICATION_TAB.VERIFICATIONS:
        onClickVerificationsTab();
        setTabQueryParam(JOB_APPLICATION_TAB.VERIFICATIONS);
        return;
      default:
        throw new Error('Unknown tab name');
    }
  };

  const getTabPanel = (tab: DetailTabSelectOption) => {
    switch (tab.value) {
      case JOB_APPLICATION_TAB.PROFILE:
        return (
          <DetailsContentViewWrapper>
            <ProfileBody
              applicationDocuments={{
                attachments: sortStoreAttachments(
                  jobApplication.attachments,
                ).map((attachment) => {
                  const downloadUrl = attachmentUrlV2({
                    jobId,
                    applicationCorrelationId:
                      jobApplication.applicationCorrelationId,
                    attachmentType: mapStoreAttachmentTypeToRequestInput(
                      attachment.type,
                    ),
                    actionType: 'download',
                    attachmentId: attachment.attachmentId,
                    version: referencesReceived,
                    language,
                  });

                  return {
                    attachmentId: attachment.attachmentId,
                    type: attachment.hirerGraphType,
                    filename: attachment.filename,
                    typeName: attachment.typeName,
                    downloadUrl,
                  };
                }),
                tracking: {
                  applicationCorrelationId:
                    jobApplication.applicationCorrelationId,
                  applicationId: jobApplication.jobApplicationId,
                  seekCandidateId: jobApplication.seekCandidateId,
                  adcentreCandidateId: jobApplication.candidateId,
                  prospectId: jobApplication.id,
                  isVerifiedId: profile?.verifiedIdentity?.fullName ? 1 : 0,
                },
              }}
              roles={transformedProfile?.roles ?? []}
              referenceChecksCount={referenceCheckCounts?.finishedCount ?? 0}
              showCareerHistoryVerificationsAlert={
                verificationStats?.workHistoryCount
                  ? verificationStats.workHistoryCount > 0
                  : false
              }
              educationItems={
                transformedProfile?.qualifications.map((education) => ({
                  id: education.id,
                  title: education.name.text,
                  institute: education.institute.text,
                  highlights: education.highlights,
                  completed: education.completed,
                  completionDate: education.completionDate,
                  verificationViewFields: education.credential?.credentialInfo,
                })) ?? []
              }
              verifiedIdentity={
                profile?.verifiedIdentity?.fullName
                  ? profile.verifiedIdentity
                  : null
              }
              licences={
                profile?.licences.map((licence) => ({
                  id: licence.id,
                  title: licence.name.text,
                  issuingOrganisation: licence.issuingOrganisation,
                  formattedDate: licence.formattedExpiryDate,
                  description: licence.description,
                  verificationViewFields: licence.credential?.credentialInfo,
                })) ?? []
              }
              employmentStatus={{
                nationalities: transformedProfile?.nationalities ?? [],
                rightsToWork:
                  transformedProfile?.rightsToWorkV2.map(
                    ({ displayLabel }) => displayLabel,
                  ) ?? [],
              }}
              applicationQuestions={{
                questions: applicationQuestions,
                allVerifications: verifications,
              }}
              skills={profile?.skills.map(({ keyword }) => keyword.text) ?? []}
              candidateFullName={jobApplication.fullName}
              jobApplicationPositionTitle={jobApplication.jobInformation.title}
              shouldShowPriorityApply={jobApplication.isPriorityApply}
              shouldShowVerifiedCredentials={shouldShowVerifiedCredentials}
              onClickVerificationsAlertLink={onClickProfileBodyVerificationsTab}
              onClickProfileTabVerificationsLearnMore={
                onClickProfileTabVerificationsLearnMore
              }
              onVerificationsAlertMount={onProfileBodyVerificationsAlertMount}
              highFitSummary={
                jobApplication.fitSummary.success
                  ? jobApplication.fitSummary.value
                  : null
              }
            />
          </DetailsContentViewWrapper>
        );
      case JOB_APPLICATION_TAB.RESUME:
      case JOB_APPLICATION_TAB.COVER_LETTER:
      case JOB_APPLICATION_TAB.SELECTION_CRITERIA:
        const attachment = tab.attachment;
        if (attachment && attachment.jobApplication) {
          return (
            <DetailsContentViewWrapper>
              <PdfViewer
                attachment={attachment}
                isVerifiedId={isVerifiedId}
                jobId={jobId}
                applicationCorrelationId={
                  jobApplication.applicationCorrelationId
                }
                applicationId={jobApplication.jobApplicationId}
                seekCandidateId={jobApplication.seekCandidateId}
                adcentreCandidateId={jobApplication.candidateId}
                isSeekReferenceApplication={isSeekReferenceApplication}
                referencesReceived={referencesReceived}
                isManuallyAddedCandidate={jobApplication.source !== 1}
                applicationSeekReferenceStatus={getApplicationSeekReferenceStatus(
                  referenceChecks,
                )}
                referenceCheckType={undefined}
              />
            </DetailsContentViewWrapper>
          );
        }
        return <TabPanel> </TabPanel>;
      case JOB_APPLICATION_TAB.REFERENCES:
        if (referenceChecks.case === 'REQUESTED') {
          return (
            <DetailsContentViewWrapper>
              <ReferencesEmptyState />
            </DetailsContentViewWrapper>
          );
        }

        const referenceAttachment = tab.attachment;
        // FIXME: Check if we need this referenceAtachment.jobApplication is needed (Same as the attachments tabs above)
        if (referenceAttachment && referenceAttachment.jobApplication) {
          return (
            <DetailsContentViewWrapper>
              <Stack space="large">
                {(function renderUnlockReferenceCheckAlert() {
                  if (referenceChecks.case !== 'LIMITED_AVAILABLE') {
                    return null;
                  }

                  if (referenceChecks.canUnlockStandardReferenceCheck) {
                    return (
                      <UnlockReferenceCheckAlert
                        onClick={() =>
                          referenceChecks.onClick({
                            actionOrigin: 'limited reference check banner',
                          })
                        }
                      />
                    );
                  }

                  return (
                    <PurchaseReferenceCheckAddonAlert
                      onClickPurchase={() =>
                        referenceChecks.onClick({
                          actionOrigin: 'limited reference check banner',
                        })
                      }
                    />
                  );
                })()}
                <PdfViewer
                  attachment={referenceAttachment}
                  isVerifiedId={isVerifiedId}
                  jobId={jobId}
                  applicationCorrelationId={
                    jobApplication.applicationCorrelationId
                  }
                  applicationId={jobApplication.jobApplicationId}
                  seekCandidateId={jobApplication.seekCandidateId}
                  adcentreCandidateId={jobApplication.candidateId}
                  isSeekReferenceApplication={isSeekReferenceApplication}
                  referencesReceived={referencesReceived}
                  isManuallyAddedCandidate={jobApplication.source !== 1}
                  applicationSeekReferenceStatus={getApplicationSeekReferenceStatus(
                    referenceChecks,
                  )}
                  referenceCheckType={
                    jobApplication.referenceCheckStatus?.referenceCheckType
                  }
                />
              </Stack>
            </DetailsContentViewWrapper>
          );
        }

        return <TabPanel> </TabPanel>;
      case JOB_APPLICATION_TAB.PREVIOUS_APPLICATIONS:
        return (
          <DetailsContentViewWrapper>
            <PreviousApplications
              jobId={jobId}
              candidateStore={candidateStore}
              isVerifiedId={isVerifiedId}
              addCustomError={addCustomError}
              locale={locale}
            />
          </DetailsContentViewWrapper>
        );
      case JOB_APPLICATION_TAB.EMAILS:
        return (
          <DetailsContentViewWrapper>
            <ProspectEmail
              prospectEmailStore={prospectEmailStore}
              locale={locale}
              addCustomError={addCustomError}
            />
          </DetailsContentViewWrapper>
        );
      case JOB_APPLICATION_TAB.NOTES_ATTACHMENTS:
        return (
          <DetailsContentViewWrapper>
            <NotesAttachments
              notes={notes}
              jobApplicationId={jobApplication.jobApplicationId}
              seekCandidateId={jobApplication.seekCandidateId}
              adcentreCandidateId={jobApplication.candidateId}
              locale={locale}
              isVerifiedId={isVerifiedId}
              jobId={jobId}
              applicationCorrelationId={jobApplication.applicationCorrelationId}
              addCustomError={addCustomError}
              updateStoreWithNotesAttachmentData={
                updateStoreWithNotesAttachmentData
              }
              userId={userId}
              source={jobApplication.source}
            />
          </DetailsContentViewWrapper>
        );
      case JOB_APPLICATION_TAB.VERIFICATIONS:
        return (
          <DetailsContentViewWrapper>
            <Verifications
              queryInput={{ adcentreProspectId: jobApplication.id, jobId }}
              onCloseVerificationsAlert={onCloseVerificationsAlert}
              onClickVerificationsAlertLearnMore={
                onClickVerificationsAlertLearnMore
              }
              onClickVerificationsTabFooterLearnMore={
                onClickVerificationsTabFooterLearnMore
              }
              onClickWhyThisCouldHappen={onClickCareerHistoryWhyThisCouldHappen}
            />
          </DetailsContentViewWrapper>
        );
      default:
        return <></>;
    }
  };

  return (
    <Box zIndex={0}>
      <TabsProvider
        id="tab-select-detail-view"
        selectedItem={jobApplication.selectedTabInDetailView}
        onChange={onTabChange}
      >
        <TabSelect testId="details-tab-selector" options={tabs} />
        <Box
          display="flex"
          flexDirection="column"
          flexGrow={1}
          background="surface"
        >
          <TabPanels>
            {tabs.map((tab) => (
              <TabPanel item={tab.value} key={tab.value}>
                {getTabPanel(tab)}
              </TabPanel>
            ))}
          </TabPanels>
        </Box>
      </TabsProvider>
    </Box>
  );
};

const DetailsContentViewWrapper: FC<PropsWithChildren> = ({ children }) => (
  <Box paddingTop="large" paddingBottom="medium" background="surface">
    {children}
  </Box>
);

export default observer(DetailsTabView);
